import React, { useState } from "react";
import { Button, Modal, Spinner, Badge } from "react-bootstrap";
import { saveAs } from "file-saver";
import JSZip from "jszip";

function CheckIcon({ checked, inverted }) {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 4,
        border: checked
          ? inverted
            ? "2px solid white"
            : "2px solid #0d6efd"
          : "2px solid #ced4da",
        backgroundColor: checked
          ? inverted
            ? "white"
            : "#0d6efd"
          : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.15s ease",
      }}
    >
      {checked && (
        <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
          <path
            d="M1 4L4.5 7.5L11 1"
            stroke={inverted ? "#0d6efd" : "white"}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

export default function DownloadModal({
  modalShow,
  setModalShow,
  monthData,
  contracts,
  measuringPointData,
}) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const filteredData = monthData
    .filter((item) => item.isMonthRetroactive === false)
    .sort((a, b) => {
      if (a.year !== b.year) return (a.year - b.year) * -1;
      return (a.month - b.month) * -1;
    });

  const getMonthName = (month, year) => {
    const names = [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ];
    return month >= 1 && month <= 12
      ? `${names[month - 1]} de ${year}`
      : "Mês inválido";
  };

  const docTitles = [
    "Relatório de desempenho mensal",
    "Análise da fatura da distribuidora",
    "Fatura da distribuidora",
    "Medição",
    "Nota de energia",
    "Projeção semanal",
  ];

  const getTitleByType = (type) => {
    const map = {
      RELATORIO_DESEMPENHO: "Relatório de desempenho mensal",
      ANALISE_FATURA_DISTRIBUIDORA: "Análise da fatura da distribuidora",
      FATURA_DISTRIBUIDORA: "Fatura da distribuidora",
      PLANILHA_MEDICAO: "Medição",
      NOTA_ENERGIA_COMPRA: "Nota de energia",
      PROJECAO: "Projeção semanal",
    };
    return map[type] || "Documento";
  };

  const toggleItem = (item) => {
    if (!item) return;
    setSelected((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const toggleGroup = (items) => {
    const valid = (Array.isArray(items) ? items : [items]).filter(Boolean);
    setSelected((prev) => {
      const allSelected = valid.every((item) => prev.includes(item));
      return allSelected
        ? prev.filter((item) => !valid.includes(item))
        : [...prev, ...valid.filter((item) => !prev.includes(item))];
    });
  };

  const isSelected = (item) => Boolean(item) && selected.includes(item);

  const isGroupSelected = (items) => {
    const valid = items.filter(Boolean);
    return valid.length > 0 && valid.every((i) => selected.includes(i));
  };

  const allContractItems = contracts.flatMap((c) => c.contract.contracts);
  const allReportItems = filteredData.flatMap((invoice) =>
    invoice.reports.filter((r) => r.type !== "MEDICAO")
  );
  const allItems = [...allContractItems, ...allReportItems];

  const handleDownload = async (docs) => {
    if (!docs.length) return;
    setLoading(true);

    if (docs.length === 1) {
      const doc = docs[0];
      if (!doc?.url) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(doc.url);
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = doc.name.includes(doc.extension)
          ? doc.name
          : `${doc.name}.${doc.extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error(`Erro ao baixar ${docs[0].name}:`, err);
      }
      setLoading(false);
      setSelected([]);
      setModalShow(false);
      return;
    }

    const zip = new JSZip();
    for (const [i, doc] of docs.entries()) {
      if (!doc?.url) continue;
      try {
        const res = await fetch(doc.url);
        const blob = await res.blob();
        const fileName = doc.name.includes(doc.extension)
          ? `${doc.name}`
          : `${doc.name}.${doc.extension}`;
        zip.file(fileName.replaceAll("/", "-"), blob);
      } catch (err) {
        console.error(`Erro ao baixar ${doc.name}:`, err);
      }
    }
    try {
      const zipFile = await zip.generateAsync({ type: "blob" });
      saveAs(zipFile, "Documentos.zip");
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
    setSelected([]);
    setModalShow(false);
  };

  return (
    <Modal
      show={modalShow}
      onHide={() => {
        setModalShow(false);
        setSelected([]);
      }}
      size="xl"
      centered
    >
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2">
          <Modal.Title>
            Documentos da unidade {measuringPointData?.company_name} -{" "}
            {measuringPointData?.name}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: 0 }}>
        {/* Seção de Contratos */}
        {/* <div
          className="px-4 pt-3 pb-3"
          style={{ borderBottom: "1px solid #e9ecef" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span
              className="text-uppercase text-muted fw-semibold"
              style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}
            >
              Contratos
            </span>
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              style={{ fontSize: "0.8rem" }}
              onClick={() => toggleGroup(allContractItems)}
            >
              {isGroupSelected(allContractItems)
                ? "Desmarcar todos"
                : "Selecionar todos"}
            </button>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {contracts.map((contract) =>
              contract.contract.contracts.map((c, idx) => {
                const sel = isSelected(c);
                return (
                  <div
                    key={idx}
                    title={c?.name}
                    onClick={() => toggleItem(c)}
                    className="d-flex align-items-center gap-2 px-3 py-2 rounded"
                    style={{
                      backgroundColor: sel ? "#0d6efd" : "#fff",
                      border: `1.5px solid ${sel ? "#0d6efd" : "#dee2e6"}`,
                      color: sel ? "#fff" : "#212529",
                      cursor: c ? "pointer" : "default",
                      maxWidth: 300,
                      userSelect: "none",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span
                      className="text-truncate"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {c?.name}
                    </span>
                    <CheckIcon checked={sel} inverted={sel} />
                  </div>
                );
              })
            )}
          </div>
        </div> */}

        {/* Seção de Relatórios Mensais */}
        <div
          className="px-4 pt-3 pb-3"
          style={{ maxHeight: 480, overflowY: "auto" }}
        >
          <span
            className="text-uppercase text-muted fw-semibold d-block mb-3"
            style={{ fontSize: "0.7rem", letterSpacing: "0.08em" }}
          >
            Documentos mensais
          </span>
          <div className="row g-3">
            {filteredData.map((invoice, i) => {
              const invoiceReports = docTitles.map((title) => {
                const matches = invoice.reports.filter(
                  (r) => getTitleByType(r.type) === title
                );
                return {
                  title,
                  data: matches.length <= 1 ? matches[0] : matches,
                };
              });
              const selectableItems = invoice.reports.filter(
                (r) => r.type !== "MEDICAO"
              );
              const monthAllSelected =
                selectableItems.length > 0 &&
                selectableItems.every((r) => selected.includes(r));

              return (
                <div key={i} className="col-12 col-md-6 col-lg-4">
                  <div
                    className="p-3 rounded h-100"
                    style={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="fw-semibold"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {getMonthName(invoice.month, invoice.year)}
                      </span>
                      <button
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        style={{ fontSize: "0.75rem" }}
                        onClick={() => toggleGroup(selectableItems)}
                      >
                        {monthAllSelected ? "Desmarcar" : "Selec. todos"}
                      </button>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      {invoiceReports.map((report, idx) => {
                        const available = Array.isArray(report.data)
                          ? report.data.length > 0
                          : Boolean(report.data);
                        const checked = Array.isArray(report.data)
                          ? report.data.length > 0 &&
                            report.data.every((d) => selected.includes(d))
                          : isSelected(report.data);

                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              if (!available) return;
                              Array.isArray(report.data)
                                ? toggleGroup(report.data)
                                : toggleItem(report.data);
                            }}
                            className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                            style={{
                              backgroundColor: checked
                                ? "#0d6efd"
                                : available
                                ? "#fff"
                                : "#f3f4f6",
                              border: `1.5px solid ${
                                checked ? "#0d6efd" : "#dee2e6"
                              }`,
                              color: checked
                                ? "#fff"
                                : available
                                ? "#212529"
                                : "#9ca3af",
                              cursor: available ? "pointer" : "default",
                              opacity: available ? 1 : 0.6,
                              userSelect: "none",
                              transition: "all 0.15s ease",
                              minHeight: 38,
                            }}
                          >
                            <span style={{ fontSize: "0.82rem" }}>
                              {report.title}
                            </span>
                            {available ? (
                              <CheckIcon checked={checked} inverted={checked} />
                            ) : (
                              <span
                                style={{
                                  fontSize: "0.7rem",
                                  color: "#adb5bd",
                                }}
                              >
                                Indisponível
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        className="d-flex justify-content-between align-items-center"
        style={{ borderTop: "1px solid #e9ecef", marginTop: "4px" }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center gap-2"
            style={{
              cursor: "pointer",
              userSelect: "none",
              gap: "8px",
            }}
            onClick={() => toggleGroup(allItems)}
          >
            <CheckIcon checked={isGroupSelected(allItems)} />
            <span style={{ fontSize: "0.875rem" }}>Selecionar tudo</span>
          </div>

          {selected.length > 0 && (
            <div>
              <Badge bg="primary" pill style={{ fontSize: "0.8rem" }}>
                {selected.length} selecionado{selected.length !== 1 ? "s" : ""}
              </Badge>

              <button
                className="btn btn-link btn-sm p-0 text-muted"
                style={{
                  fontSize: "0.8rem",
                  textDecoration: "none",
                  marginLeft: "8px",
                }}
                onClick={() => setSelected([])}
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
        <Button
          onClick={() => handleDownload(selected)}
          disabled={selected.length === 0 || loading}
        >
          {loading ? (
            <Spinner animation="border" size="sm" role="status" />
          ) : selected.length > 0 ? (
            `Baixar (${selected.length})`
          ) : (
            "Baixar selecionados"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
